import React, { useMemo } from 'react';
import { Flex, InputNumber, Typography } from '@duxton-web/ui';
import _ from 'lodash';
import styled from 'styled-components';

import {
  useVariableContext,
  useVariableDispatchContext,
} from '@/features/variable/contexts/variable-context';
import {
  getSchemaTypeByVariableType,
  JSONSchema,
  SchemaType,
} from '@/features/variable/settings/validation';
import { variableJsonSchemaUpdate } from '@/features/variable/store/actions/variable/action-creators';
import { isFieldChanged } from '@/features/variable/utils';
import { VariableInfo } from '@/lib/PlannerDTOs';

export const emptyRange = (min: number, max: number): boolean => {
  return min == null && max == null;
};

export const isValidRange = (min: number, max: number): boolean => {
  if (min == null && max == null) return true;

  if (min != null && max != null) return max && min < max;

  return true;
};

const getVariableSchema = (variable: VariableInfo): JSONSchema => {
  if (!variable?.metadata?.jsonSchema)
    return {
      type: getSchemaTypeByVariableType(variable) as any,
      minLength: null,
      maxLength: null,
    };

  return JSON.parse(variable.metadata.jsonSchema);
};

const Error = () => {
  const { variable } = useVariableContext();

  const schema = getVariableSchema(variable);
  const { minLength, maxLength } = schema;

  if (!isValidRange(minLength, maxLength))
    return (
      <Typography.Text type="danger">
        Min cannot be greater or equal than Max
      </Typography.Text>
    );

  if (emptyRange(minLength, maxLength))
    return (
      <Typography.Text type="danger">
        Min and/or Max is/are required
      </Typography.Text>
    );

  return null;
};

const StringRange = () => {
  const { variable, disabled, schemaDiff } = useVariableContext();
  const dispatch = useVariableDispatchContext();

  const schema = getVariableSchema(variable);

  const debouncedHandleRangeChange = useMemo(
    () =>
      _.debounce(async (min: number, max: number) => {
        const updatedSchema = {
          type: SchemaType.STRING,
        };

        if (min != null) updatedSchema['minLength'] = min;

        if (max != null) updatedSchema['maxLength'] = max;

        dispatch(
          variableJsonSchemaUpdate(
            updatedSchema,
            !emptyRange(min, max) && isValidRange(min, max),
          ),
        );
      }, 500),
    [],
  );

  const rangeChange = (min: number, max: number) => {
    debouncedHandleRangeChange(min, max);
  };

  return (
    <div
      id="validation-range-container"
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <SecondaryText>Range of characters</SecondaryText>
      <Flex align="flex-start" gap={8}>
        <InputNumber
          disabled={disabled}
          data-testid="string-range-min"
          className={`validation-prefix-input-number ${
            isFieldChanged(schemaDiff, 'minLength')
              ? 'changelog-item-variable-modified'
              : ''
          }`}
          prefix="MIN"
          min={0}
          onChange={(e) => {
            rangeChange(
              e ? parseInt(e.toString(), 10) : null,
              schema.maxLength,
            );
          }}
          precision={0}
          value={schema.minLength}
          style={{
            width: 210,
          }}
        />
        <Flex vertical>
          <InputNumber
            disabled={disabled}
            data-testid="string-range-max"
            className={`validation-prefix-input-number ${
              isFieldChanged(schemaDiff, 'maxLength')
                ? 'changelog-item-variable-modified'
                : ''
            }`}
            prefix="MAX"
            min={0}
            onChange={(e) => {
              rangeChange(
                schema.minLength,
                e ? parseInt(e.toString(), 10) : null,
              );
            }}
            precision={0}
            status={
              isValidRange(schema.minLength, schema.maxLength) ? '' : 'error'
            }
            value={schema.maxLength}
            style={{
              width: 210,
            }}
          />
        </Flex>
      </Flex>
      <Error />
    </div>
  );
};

const SecondaryText = styled(Typography.Metadata)`
  color: #a3a3a3;
`;

export default StringRange;
